import React, { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Button from "../../../components/Button";
import { axiosInstance } from "../../../utils/axios";

export default function AbaNinjaTonerModal({ selectedToners, onClose }) {
  const [abaForm, setAbaForm] = useState({
    used_photocopiers: "Toner Invoice",
    photocopiers_sets: "0",
    finishers_count: "0",
    collis_count: String(selectedToners.length),
    weight: String(
      selectedToners.reduce((sum, t) => sum + Number(t.weight || 0), 0)
    ),
    payment_terms: "In advance",
    made_in: "China",
  });

  const [isSending, setIsSending] = useState(false);

  // We group toners by toner_model + price
  const groupedToners = useMemo(() => {
    const map = {};
    selectedToners.forEach((toner) => {
      const model = toner.toner_model || toner.machine_model || "Toner";
      const price = parseFloat(toner.price) || 0;
      const key = `${model}||${price}`;
      if (!map[key]) {
        map[key] = {
          key,
          model,
          price,
          count: 0,
        };
      }
      map[key].count += 1;
    });
    return Object.values(map);
  }, [selectedToners]);

  const handleSubmit = async () => {
    setIsSending(true);
    try {
      const ABA_ACCOUNT_UUID = window.REACT_APP_ABANINJA_ACCOUNT_UUID;
      const ABA_API_KEY = window.REACT_APP_ABANINJA_API_KEY;
      const abaHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ABA_API_KEY}`,
      };

      // ── 1. Fetch bank accounts & first person as dummy receiver ──
      const [bankRes, abaPersonsRes] = await Promise.all([
        fetch(
          `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/finances/v2/bank-accounts`,
          { headers: abaHeaders }
        ),
        fetch(
          `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/persons?limit=1`,
          { headers: abaHeaders }
        ),
      ]);
      
      if (!bankRes.ok)
        throw new Error("Failed to fetch AbaNinja bank accounts.");
      const bankData = await bankRes.json();
      const bankAccounts = bankData?.data ?? [];

      let dummyReceiver = null;
      if (abaPersonsRes.ok) {
        const personsData = await abaPersonsRes.json();
        if (personsData?.data?.length > 0) {
          dummyReceiver = personsData.data[0];
        }
      }

      // ── 2. Find the correct bank ──
      const targetBankName = "ZKB CHF";
      const bank = bankAccounts.find((b) =>
        b.name?.toLowerCase().includes(targetBankName.toLowerCase())
      );
      if (!bank?.iban) {
        throw new Error(
          `Bank account "${targetBankName}" with a valid IBAN was not found in AbaNinja. ` +
            `Available banks: ${bankAccounts.map((b) => b.name).join(", ")}`
        );
      }

      // ── 3. Calculate dates ──
      const today = new Date();
      const invoiceDateStr = today.toISOString().split("T")[0];
      const due = new Date(today);
      due.setDate(due.getDate() + 30);
      const dueDateStr = due.toISOString().split("T")[0];

      // ── 4. Build invoice positions ──
      const positions = [];
      let posNum = 1;

      groupedToners.forEach((group) => {
        const singlePrice = group.price;
        const positionTotal = singlePrice * group.count;

        positions.push({
          kind: "product",
          positionNumber: posNum++,
          productNumber: group.model,
          productDescription: `Toner: ${group.model}`,
          quantity: group.count,
          singlePrice,
          positionTotal,
          discount: { amount: 0 },
          vat: { percentage: 0, amount: 0 },
        });
      });

      const documentTotal = positions.reduce(
        (sum, p) => sum + (p.positionTotal || 0),
        0
      );

      // ── 5. Build the AbaNinja invoice payload ──
      const invoicePayload = {
        documents: [
          {
            isTemplate: false,
            ...(dummyReceiver && {
              receiver: {
                addressUuid: dummyReceiver.addresses?.[0]?.uuid,
                personUuid: dummyReceiver.uuid,
              },
            }),
            currencyCode: "CHF",
            title: `Toner Invoice - ${Date.now()}`,
            reference: "Toner Order",
            invoiceDate: invoiceDateStr,
            dueDate: dueDateStr,
            pricesIncludeVat: false,
            documentTotal,
            paymentInstructions: {
              iban: bank.iban,
            },
            publicNotes: abaForm.used_photocopiers || "",
            footerText: "",
            positions,
          },
        ],
      };

      // ── 6. POST new invoice to AbaNinja ──
      const abaRes = await fetch(
        `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices`,
        {
          method: "POST",
          headers: abaHeaders,
          body: JSON.stringify(invoicePayload),
        }
      );
      const abaResult = await abaRes.json();
      if (!abaRes.ok) {
        console.error("AbaNinja error response:", abaResult);
        throw new Error(
          abaResult?.message ||
            abaResult?.meta?.errors?.[0]?.error ||
            "AbaNinja invoice creation failed."
        );
      }

      toast.success("Successfully sent to AbaNinja!");
      onClose();
    } catch (err) {
      console.error("AbaNinja send error:", err);
      toast.error(err.message || "Failed to send to AbaNinja.");
    } finally {
      setIsSending(false);
    }
  };

  const inputClass =
    "w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Model
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Total Units
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Price per Unit
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {groupedToners.length > 0 ? (
              <>
                {groupedToners.map((group, i) => (
                  <tr
                    key={group.key}
                    className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {group.model}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                      {group.count}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {group.price}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-4 py-2 text-sm text-gray-700 text-right">
                    Total toners:
                  </td>
                  <td colSpan={2} className="px-4 py-2 text-sm text-gray-900">
                    {selectedToners.length}
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={3} className="text-center py-4">
                  No toners selected
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {[
          { key: "used_photocopiers", label: "Public Notes" },
          { key: "collis_count", label: "Collis" },
          { key: "weight", label: "Weight (total weight)" },
          { key: "payment_terms", label: "Payment terms" },
          { key: "made_in", label: "Made in" },
        ].map(({ key, label }, index, array) => (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label}
            </label>
            <input
              id={`abaninja-toner-input-${index}`}
              type="text"
              value={abaForm[key]}
              onChange={(e) =>
                setAbaForm((p) => ({ ...p, [key]: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (index < array.length - 1) {
                    document
                      .getElementById(`abaninja-toner-input-${index + 1}`)
                      ?.focus();
                  } else {
                    document.getElementById("abaninja-toner-submit-btn")?.focus();
                  }
                }
              }}
              className={inputClass}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="light" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          id="abaninja-toner-submit-btn"
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={isSending || selectedToners.length === 0}
        >
          {isSending ? "Sending..." : "Send to AbaNinja"}
        </Button>
      </div>
    </div>
  );
}
